/**
 * /technical-documentation-template — SEO template page for "technical
 * documentation" (~53 traffic but vol ~10k, KD low, medium fit — docs).
 * Hands over the structure of good technical docs, then the Draftlize turn:
 * technical docs describe a system that changes, and the doc drifts from the
 * code the moment a decision behind it moves.
 *
 * English-only + inline. Chinese variant in TechnicalDocumentationTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function TechnicalDocumentationTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/technical-documentation-template"
      documentTitle="Technical Documentation Template · Draftlize"
      documentDescription="A technical documentation template you can copy — overview, getting started, architecture, API reference, configuration, runbook, troubleshooting — plus how to keep docs from drifting from the code."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Technical docs</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A technical documentation template<br/>
            <em>that doesn&apos;t rot past the code.</em>
          </h1>
          <p className="lede">
            You came for a <em>technical documentation template</em> — the structure of a doc that a new engineer can actually onboard from. It&apos;s below; copy it. But technical docs have the worst drift problem of any document: they describe a system that changes every day, and the doc is updated by hand, when someone remembers, which is rarely. Within a sprint the docs and the code disagree, and a reader can&apos;t tell which paragraphs to trust. Draftlize gives you the same structure, with the decisions behind the docs as cards that flag when the system moves past them.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Eight sections. <em>Copy them anywhere.</em></h2>
          </div>
          <p>Good technical documentation answers what a reader needs in roughly this order — from &quot;what is this&quot; to &quot;it&apos;s on fire, what do I do.&quot; Not every doc needs all eight; skip what doesn&apos;t apply.</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'Overview & purpose', hint: 'What & why', body: 'What this system or component is, the problem it solves, and who it\'s for — in a few sentences a newcomer can read first. If a reader can\'t tell whether this doc is relevant to them from the top, the rest won\'t get read.' },
            { k: 'Getting started', hint: 'Hello world', body: 'The fastest path to a working setup — prerequisites, install, a minimal example that runs. The highest-traffic section; optimize it ruthlessly, because a failed first five minutes is where most readers leave.' },
            { k: 'Architecture', hint: 'How it fits', body: 'The components, how they interact, and the key decisions behind the shape — ideally with a diagram. This is the section that saves an engineer a week, and the one most tied to decisions that change.' },
            { k: 'API / reference', hint: 'The details', body: 'Endpoints, functions, parameters, return values, errors. Exhaustive and precise; this is lookup material, not narrative. Best generated from the source where possible so it can\'t silently fall behind.' },
            { k: 'Configuration', hint: 'Knobs', body: 'Environment variables, settings, flags — what each does, defaults, and valid values. The section people grep for at 2am, so completeness beats prose.' },
            { k: 'Runbook / operations', hint: 'Run it', body: 'How to deploy, monitor, scale, and recover — the operational reality, not just the happy path. What the dashboards mean, what the alerts mean, what to do when each fires.' },
            { k: 'Troubleshooting / FAQ', hint: 'When it breaks', body: 'The common failures and their fixes, written from real incidents. The section that turns a 30-minute outage into a 3-minute one — and the first to go stale as failure modes change.' },
            { k: 'Changelog & owners', hint: 'Trust signals', body: 'What changed, when, and who owns this. The metadata that tells a reader whether to trust the doc at all — a doc last touched a year ago is a warning, not a reference.' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.06em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          For the design-decision layer above this, see the <a href="/technical-design-document" style={{ color: 'var(--amber)' }}>technical design document</a>; for where docs live, the <a href="/best-knowledge-base-software" style={{ color: 'var(--amber)' }}>knowledge base</a>.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why technical docs <em>rot fastest.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">They describe a moving target</h3>
            <p>Code changes daily; docs change when someone remembers. The gap opens immediately and compounds. Unlike most documents, technical docs have an authoritative source of truth right next to them — the code — and the doc is always the one that&apos;s behind.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">You can&apos;t tell which parts are stale</h3>
            <p>A doc that&apos;s 80% correct is more dangerous than one that&apos;s missing, because the reader trusts the wrong 20%. Without a signal for which sections are current, the whole doc gets quietly distrusted — and then people stop writing docs at all.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">The decisions behind the docs aren&apos;t linked</h3>
            <p>The architecture section explains a design decision — why this queue, why this boundary. When that decision is reversed in code, nothing tells the doc. The explanation outlives the thing it explained, confidently describing a system that no longer works that way.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same docs, <em>wired in.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Doc sections link to the decisions they explain</h3>
            <p>In Draftlize the architecture and design rationale in your docs are cards linked to the actual decisions — &quot;we use a queue here because…&quot; points at the decision card, not a paragraph retyped from memory. The doc references the decision instead of duplicating it.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">A decision changes, the doc flags stale</h3>
            <p>Reverse the design decision in code and the doc section that explained it auto-flags stale — the way a build system invalidates everything downstream of a changed file. The reader gets a signal, instead of trusting a confident paragraph that&apos;s now wrong.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your coding agent reads current docs</h3>
            <p>Claude Code or Cursor reads the linked decisions over MCP before it touches the code — so the agent works from the architecture as it is now, and when it changes the design, it can write the decision back, keeping the doc and the code on the same version.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Technical docs are the only document with the truth sitting right next to them in the code — and they&apos;re always the one that&apos;s behind.
        </blockquote>
        <cite>Keep the structure. Link the docs to the decisions, so the code can flag them stale.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What should technical documentation include?', a: 'An overview and purpose, a getting-started guide, an architecture section, an API or reference section, configuration, a runbook for operations, troubleshooting, and a changelog with owners. Not every doc needs all of them — match the sections to what the reader actually needs.' },
            { q: 'What is the difference between technical documentation and a technical design document?', a: 'A technical design document (TDD) is written before building to propose and align on an approach. Technical documentation describes the system as built, for people who use or maintain it. The TDD is a decision; the documentation is a reference.' },
            { q: 'How do you keep technical documentation from going out of date?', a: 'Generate reference material from the source where possible, keep docs close to the code (docs-as-code), and link the explanations to the decisions they rest on so a change flags the affected sections. The failure mode is hand-maintained prose with no signal for which parts are still true.' },
            { q: 'What is "docs as code"?', a: 'Treating documentation like source code — written in plain-text formats like Markdown, stored in version control next to the code, reviewed in pull requests, and built with a pipeline. It keeps docs closer to changes and makes drift more visible, though it does not eliminate it on its own.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Keep docs in sync with the code <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Use the structure above, or keep the decisions behind your docs as cards in Draftlize linked to the design — so when the code reverses a decision, the doc section that explained it flags stale, and your coding agent always reads the current architecture.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
